import TTLCache from '@isaacs/ttlcache';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchHolterEvents, formatEventStrip } from '@/entities/event';
import {
  EMITTER_CONSTANTS,
  KEY_CANCEL,
  KEY_RECOIL,
  STRIP_ARTIFACT_THUMBNAIL_INFO,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  BEAT_HR_TAB_ID,
  selectCombinedEvents,
  selectHolterEvents,
  selectIsLoadingStrip,
  selectIsSkipSelectThumbnail,
  selectPageIndex,
  selectSelectedDateValue,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { getStripIndex, getStripPosition } from '@/entities/strip';
import { logError } from '@/shared/lib/apiHelpers';
import type { SelectionAreaWrapperHandle } from '@/shared/ui';
import { getCachedDataSlice, getCurrentChunk } from './handler';
import type { StripEventsProps } from './stripEvents';

const artifactStripEventsCache = new TTLCache({ ttl: 3600000, max: 3 });

/**
 * Drives the artifact strip thumbnail grid of the beats tab: paged strip loading, selection, keyboard navigation and thumbnail render settings. Binds report-store reads and writes to the tab-1 key, fetches a day's artifact events with a chunked prefetch cache, keeps the selected strip and its grid position in sync across page changes, and subscribes to reload and paging emitter events plus a keydown listener that is active only while the tab is hovered.
 *
 * @param props - Study and profile identifiers the artifact event queries are scoped to.
 * @returns The strip grid state and handlers: holter events, loading flag, selected strip, selection setter and area ref, click handler, selection flag and the thumbnail high-pass, gain, channel and size settings.
 */
const useStripEvents = ({
  studyId,
  profileId,
  timezoneOffset,
}: Pick<StripEventsProps, 'studyId' | 'profileId' | 'timezoneOffset'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Sets the artifact strip grid's page index. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Sets the currently selected artifact strip. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const isSkipSelectThumbnail = useReportStore(selectIsSkipSelectThumbnail(keyRecoil));
  const setIsSkipSelectThumbnail = useReportStore((s) => s.actions.setIsSkipSelectThumbnail);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Sets whether the artifact strip grid is loading. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Writes the multi-selected artifact strips for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const setHolterEventsAction = useReportStore((s) => s.actions.setHolterEvents);
  /**
   * Writes the artifact strip tab's holter event list. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setHolterEvents = (v: Parameters<typeof setHolterEventsAction>[1]) =>
    setHolterEventsAction(keyRecoil, v);
  /**
   * Reads the merged artifact event list for this tab straight off the report store. Applies the combined-events selector to a snapshot rather than subscribing, so callers inside effects see the current value.
   *
   * @returns The combined artifact events for this tab.
   */
  const getCombinedEvents = () => selectCombinedEvents(keyRecoil)(useReportStore.getState());
  /**
   * Reads the day's total artifact strip count from the report store. Takes a snapshot instead of subscribing, so paging and caching logic can read it without re-rendering.
   *
   * @returns The total number of artifact strips for the selected day.
   */
  const getTotalStrip = () => useReportStore.getState().totalStrip;
  const setSelectedEventInfoIndexAction = useReportStore(
    (s) => s.actions.setSelectedEventInfoIndex,
  );
  /**
   * Sets which info row of the selected artifact strip is highlighted. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventInfoIndex = (v: Parameters<typeof setSelectedEventInfoIndexAction>[1]) =>
    setSelectedEventInfoIndexAction(keyRecoil, v);

  const [selected, setSelected] = useState(new Set<string>());

  const selectionAreaWrapperRef = useRef<SelectionAreaWrapperHandle>(null);
  const arrowKeyToPrevPage = useRef(false);
  const prevPageIndex = useRef(pageIndex.index);
  const stripPosition = useRef({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cache = useRef(new Map()); // Create a cache

  let isActiveQuery: boolean;

  /**
   * Prefetches artifact strip pages around the one being viewed and folds them into the shared artifact strip cache. Reads any already cached entry, issues one holter-events request per requested chunk with a skip and a clamped take, merges the results under chunk keys, and writes the entry back; on failure it clears the in-flight marker for this chunk key and logs the error.
   *
   * @param holterEventsFilter - Base study, profile, type and date-range filter each chunk request is built from.
   * @param totalStrip - Total artifact strips available for the day, used to clamp the last chunk's take.
   * @param chunkSize - Number of strips per cached chunk.
   * @param cacheChunks - Chunk indices still missing from the cache; nothing is fetched when empty.
   * @param cacheKey - Cache entry key for the currently selected day range.
   * @param _cacheKey - In-flight marker key cleared from the local ref cache when the prefetch fails.
   * @param lastResult - Previously read cache entry used as the merge base when the store returns nothing.
   * @returns A promise resolving once the requested chunks have been fetched and stored, or immediately when there is nothing to prefetch.
   */
  const fetchBackgroundData = async ({
    holterEventsFilter,
    totalStrip,
    chunkSize,
    cacheChunks,
    cacheKey,
    _cacheKey,
    lastResult = {} as any,
  }: {
    holterEventsFilter: any;
    totalStrip: any;
    chunkSize: any;
    cacheChunks: any;
    cacheKey: any;
    _cacheKey: any;
    lastResult?: any;
  }) => {
    if (_.isEmpty(cacheChunks)) {
      return;
    }

    try {
      const getCached = ((await artifactStripEventsCache.get(cacheKey)) as any) || lastResult || {};
      const stripCacheData = {
        events: getCached.events || {},
        chunks: [...(getCached?.chunks || []), ...(cacheChunks || [])],
      };
      const promisses: any[] = [];
      _.forEach(cacheChunks, (chunk: any) => {
        const skip = chunk * chunkSize;
        const take = Math.min(chunkSize, totalStrip - skip);
        if (take <= 0) {
          return;
        }
        const filter = {
          ...holterEventsFilter,
          skip,
        };

        promisses.push(fetchHolterEvents(filter, take, false, KEY_CANCEL.API_HOLTER_AI, true));
      });
      const results = await Promise.all(promisses);

      _.forEach(results, (result: any, idx) => {
        _.assign(stripCacheData.events, {
          [`chunk-${cacheChunks[idx]}`]: result?.events || [],
        });
      });
      artifactStripEventsCache.set(cacheKey, stripCacheData);
    } catch (err) {
      cache.current.set(_cacheKey, false);
      // logError(msg, error) (handler.ts, Task 7) requires both args; this call site only passes one - pre-existing.
      (logError as any)(err);
    }
  };

  const handleCacheData = ({
    holterEventsFilter,
    totalStrip,
    chunkSize,
    currentChunk,
    lastResult,
    cacheKey,
  }: {
    holterEventsFilter: any;
    totalStrip: any;
    chunkSize: any;
    currentChunk: any;
    lastResult: any;
    cacheKey: any;
  }) => {
    const { chunks } = lastResult || {};
    const hasCurrnetChunk = chunks?.includes(currentChunk);
    const hasNextCurrentChunk = chunks?.includes(currentChunk + 1);
    const hasPrevCurrentChunk = chunks?.includes(currentChunk - 1);
    if (hasCurrnetChunk && hasNextCurrentChunk && hasPrevCurrentChunk) {
      return;
    }
    const cacheChunks = [];
    if (!hasPrevCurrentChunk && currentChunk - 1 >= 0) {
      cacheChunks.push(currentChunk - 1);
    }
    if (!hasCurrnetChunk) {
      cacheChunks.push(currentChunk);
    }
    if (!hasNextCurrentChunk) {
      cacheChunks.push(currentChunk + 1);
    }

    const _cacheKey = `_cacheKey-${cacheKey}-${currentChunk}`;
    if (!_.isEmpty(cacheChunks) && !cache.current.has(_cacheKey)) {
      cache.current.set(_cacheKey, true);
      fetchBackgroundData({
        holterEventsFilter,
        totalStrip,
        chunkSize,
        cacheChunks,
        cacheKey,
        _cacheKey,
        lastResult,
      });
    }
  };

  /**
   * Loads one page of artifact strips for the selected day into the tab's holter event list. Builds a day-bounded holter-events filter from the current page index, serves the page from the chunked artifact cache when the needed chunk is present, otherwise queries the server, triggers a background prefetch of the neighbouring chunks, formats the strips and reconciles the selected strip and its grid position before clearing the loading flag.
   *
   * @returns A promise resolving once the page has been stored and the loading flag cleared.
   */
  const fetchEvents = async () => {
    const chunkSize = 100;
    setIsLoadingStrip(true);
    try {
      let downloadData: any[] = [];
      const type = [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT];
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;

      const startSearchDate = moment(selectedDateValue).toISOString();
      const stopSearchDate = moment(selectedDateValue).add(1, 'days').toISOString();

      const timeRange = {
        start: startSearchDate,
        stop: stopSearchDate,
      };

      const holterEventsFilter = {
        studyId,
        profileId,
        types: type,
        startRange: timeRange,
        skip: validatePage * STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit,
      };

      const timeCache = `${moment(startSearchDate).valueOf()}-${moment(stopSearchDate).valueOf()}`;
      const startIndex = validatePage * STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit;
      const currentChunk = getCurrentChunk(startIndex, chunkSize);
      const cacheKey = `artifactStripEventsCache-${timeCache}`;
      const currentCacheData: any = artifactStripEventsCache.has(cacheKey)
        ? artifactStripEventsCache.get(cacheKey)
        : {};
      const endIndex = (validatePage + 1) * STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit;
      const isChunkOverPage =
        Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize) > 0;
      const canUseCache = !isChunkOverPage || currentCacheData?.chunks?.includes(currentChunk + 1);
      const totalStrip = getTotalStrip();
      if (
        artifactStripEventsCache.has(cacheKey) &&
        currentCacheData?.chunks?.includes(currentChunk) &&
        canUseCache
      ) {
        // Apply the filter before slicing the data
        const { events } = artifactStripEventsCache.get(cacheKey) as any;
        const slicedData = getCachedDataSlice(
          events,
          startIndex,
          endIndex,
          currentChunk,
          chunkSize,
        );
        downloadData = [...slicedData];
      } else {
        const { events } = await fetchHolterEvents(
          holterEventsFilter,
          STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit,
          true,
          KEY_CANCEL.API_HOLTER_AI,
          false,
        );
        downloadData = [...events];
      }
      if (totalStrip) {
        handleCacheData({
          holterEventsFilter,
          totalStrip,
          chunkSize,
          currentChunk,
          lastResult: currentCacheData,
          cacheKey,
        });
      }

      const newHolterEvents = formatEventStrip(downloadData);
      if (isActiveQuery) {
        // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark, but
        // IEcgBookmark/IBeat don't declare isNew/idEvent, which this artifact-events flow always reads - pre-existing.
        if (!(selectedStrip as any)?.isNew) {
          if (pageIndex.index < prevPageIndex.current && arrowKeyToPrevPage.current) {
            stripPosition.current = getStripPosition(
              newHolterEvents.length - 1,
              STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip((newHolterEvents[newHolterEvents.length - 1] || null) as any);
          } else if (isSkipSelectThumbnail) {
            setIsSkipSelectThumbnail(keyRecoil, false);
            setSelectedStrip(null as any);
          } else {
            const selectedStripIndex = _.findIndex(
              newHolterEvents,
              (x) => x.idEvent === (selectedStrip as any)?.idEvent,
            );
            const tempSelectedStrip =
              selectedStripIndex === -1
                ? newHolterEvents[0] || null
                : newHolterEvents[selectedStripIndex];
            stripPosition.current = getStripPosition(
              selectedStripIndex === -1 ? 0 : selectedStripIndex,
              STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip(tempSelectedStrip as any);
          }
          arrowKeyToPrevPage.current = false;
          prevPageIndex.current = pageIndex.index;
        }
        // formatEventStrip's return shape (nested originalData, RhythmEvents/handler.ts) doesn't line up
        // with IEvent[] (Recoil/index.ts, Task 7's holterEventsState) - pre-existing.
        setHolterEvents(newHolterEvents as any);
        setIsLoadingStrip(false);
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
      setIsLoadingStrip(false);
    }
  };

  // Handle drag select
  /**
   * Replaces the set of artifact strip ids currently selected in the thumbnail grid. Stores the incoming set in local component state.
   *
   * @param newSelected - Ids of the strips that should now be selected.
   * @returns Nothing.
   */
  const setSelectedEvent = (newSelected: Set<string>) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedEvents: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * Resolves the artifact event a strip element refers to. Scans the loaded holter events for one whose numeric id matches the id read off the element, tolerating an empty list.
       *
       * @returns The matching holter event, or undefined when none carries that id.
       */
      const selectedEvent = (holterEvents || []).find((event) => event.id === Number(id));
      if (selectedEvent) {
        selectedEvents.push(selectedEvent);
      }
    });
    setSelectedMultipleStrip((prev: any) => {
      if (prev.length === 0 && selectedEvents.length === 0) {
        return prev;
      }
      return selectedEvents;
    });
  }, [selected, holterEvents]);

  /**
   * Selects the artifact strip a user clicked in the thumbnail grid. Resets the event info index to the first entry and, only when the clicked strip differs from the current one, records its row and column position and makes it the selected strip.
   *
   * @param event - The artifact event backing the clicked thumbnail.
   * @param index - Position of the thumbnail in the grid, used to derive its row and column.
   * @returns Nothing.
   */
  const handleClickSelectEvent = useCallback((event: any, index: any) => {
    setSelectedEventInfoIndex(0);
    setSelectedStrip((prev) => {
      if (event?.id !== prev?.id) {
        stripPosition.current = getStripPosition(index, STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow);
        return event;
      }
      return prev;
    });
  }, []);

  useEffect(
    () => () => {
      storeTimeout.removeStore(selectedDateValue, timeoutRef.current);
    },
    [selectedDateValue],
  );

  /**
   * Drops every cached artifact strip page so the next fetch goes to the server. Clears both the hook-local in-flight marker map and the shared artifact strip events cache.
   *
   * @returns Nothing.
   */
  const restCacheStripEvents = () => {
    cache.current.clear();
    artifactStripEventsCache.clear();
  };

  useEffect(() => {
    restCacheStripEvents();
  }, [isRenderViewerEcg]);

  useEffect(() => {
    isActiveQuery = true;
    if (selectedDateValue && isRenderViewerEcg) {
      fetchEvents();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [selectedDateValue, pageIndex, isRenderViewerEcg]);

  /**
   * Reloads the artifact strips after an external change invalidates them. Clears the cached strip pages and re-fetches the current page whenever a day is selected.
   *
   * @returns Nothing.
   */
  const handleReloadStripsArtifact = () => {
    restCacheStripEvents();
    if (selectedDateValue) {
      fetchEvents();
    }
  };

  useEmitter(EMITTER_CONSTANTS.WILL_CLEAR_CACHE, handleReloadStripsArtifact, [selectedDateValue]);

  useEffect(() => {
    /**
     * Moves the artifact strip selection with the arrow keys and clears it on Escape. Ignores repeats, keys pressed while strips are loading and keys pressed outside the hovered beats tab, then steps the grid row or column, selects the strip at the new position, and emits the next or previous page event when the move runs off the end of the page.
     *
     * @param event - The keyboard event being handled.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index > holterEvents.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_NEXT);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_PREV);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_ARTIFACT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [holterEvents, isLoadingStrip]);

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const currentStripIndexInList = column + row * STRIP_ARTIFACT_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList +
      pageIndex.index * STRIP_ARTIFACT_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(currentEventIndex / STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit);
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);

  useUpdateEffect(() => {
    if (selectedStrip && !(selectedStrip as any).isNew) {
      const combinedEvents = getCombinedEvents();
      const filterEventSameDay = _.filter(combinedEvents, (item: any) => {
        if (!item.start || !(selectedStrip as any).start) return false;
        const itemDate = convertToMomentJs(item.start, timezoneOffset)
          .startOf('days')
          .toISOString();
        const selectedDate = convertToMomentJs((selectedStrip as any).start, timezoneOffset)
          .startOf('days')
          .toISOString();
        return (
          itemDate === selectedDate &&
          (item.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
            item.oldType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT)
        );
      });
      const foundStripIndex = _.findIndex(
        filterEventSameDay,
        (x) => x.idEvent === (selectedStrip as any)?.idEvent,
      );
      if (foundStripIndex > -1) {
        const newPageIndex = Math.floor(
          foundStripIndex / STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit,
        );
        if (newPageIndex !== pageIndex.index) {
          setPageIndex({ index: newPageIndex });
        }
      }
    }
  }, [selectedStrip]);

  const isHasSelected = useMemo(
    () => !_.isEmpty(selectedMultipleStrip) || !_.isEmpty(selectedStrip),
    [selectedMultipleStrip, selectedStrip],
  );

  return {
    holterEvents,
    isLoadingStrip,
    selectedStrip,
    setSelectedEvent,
    selectionAreaWrapperRef,
    handleClickSelectEvent,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
  };
};

export default useStripEvents;
